import React, { useEffect, useMemo, useRef, useState } from 'react';
import Image from '../AppImage';
import { cn } from '../../utils/cn';

const STATUS_COLOR_MAP = {
  'on-work': 'bg-success',
  'in-vacation': 'bg-muted-foreground',
  'sick-leave': 'bg-error',
};

const SIZE_CLASS_MAP = {
  xs: 'w-8 h-8',
  sm: 'w-10 h-10',
  md: 'w-14 h-14',
  lg: 'w-16 h-16',
  xl: 'w-20 h-20',
};

const TEXT_SIZE_CLASS_MAP = {
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-lg',
  lg: 'text-xl',
  xl: 'text-2xl',
};

const loadedAvatarSources = new Set();

const EmployeeAvatar = ({
  url,
  src,
  alt = 'Employee avatar',
  name,
  firstName,
  lastName,
  status,
  showStatus = true,
  size = 'md',
  className,
  wrapperClassName,
}) => {
  const [hasError, setHasError] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isMinDelayElapsed, setIsMinDelayElapsed] = useState(false);
  const imageRef = useRef(null);
  const effectiveSrc = src ?? url ?? null;

  useEffect(() => {
    let timer;
    let isActive = true;

    setHasError(false);
    const isSourceCached = Boolean(effectiveSrc && loadedAvatarSources.has(effectiveSrc));
    setIsImageLoaded(isSourceCached);

    if (isSourceCached) {
      setIsMinDelayElapsed(true);
      return () => {
        isActive = false;
      };
    }

    if (effectiveSrc) {
      setIsMinDelayElapsed(false);

      const currentImage = imageRef.current;
      const isImageInstantlyAvailable =
        currentImage &&
        currentImage.complete &&
        currentImage.naturalWidth > 0;

      if (isImageInstantlyAvailable) {
        loadedAvatarSources.add(effectiveSrc);
        setIsImageLoaded(true);
        setIsMinDelayElapsed(true);
      } else {
        timer = setTimeout(() => {
          if (isActive) {
            setIsMinDelayElapsed(true);
          }
        }, 1000);
      }
    } else {
      setIsMinDelayElapsed(true);
    }

    return () => {
      isActive = false;
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [effectiveSrc]);

  const initials = useMemo(() => {
    const normalizedFirstName = typeof firstName === 'string' ? firstName.trim() : '';
    const normalizedLastName = typeof lastName === 'string' ? lastName.trim() : '';

    const firstInitial = normalizedFirstName.charAt(0);
    const lastInitial = normalizedLastName.charAt(0);

    if (firstInitial || lastInitial) {
      return `${firstInitial}${lastInitial}`.toUpperCase();
    }

    const normalizedName = typeof name === 'string' ? name.trim() : '';
    if (normalizedName.length === 0) {
      return '';
    }

    const parts = normalizedName.split(/\s+/).filter(Boolean);
    if (parts.length === 1) {
      const [single] = parts;
      if (!single) {
        return '';
      }
      const [firstLetter, secondLetter] = single;
      return `${firstLetter ?? ''}${secondLetter ?? ''}`.toUpperCase();
    }

    const first = parts[0]?.charAt(0) ?? '';
    const last = parts[parts.length - 1]?.charAt(0) ?? '';
    return `${first}${last}`.toUpperCase();
  }, [firstName, lastName, name]);

  const shouldRenderImageContainer = Boolean(effectiveSrc) && !hasError;
  const shouldShowImage = shouldRenderImageContainer && isImageLoaded && isMinDelayElapsed;
  const sizeClasses = SIZE_CLASS_MAP[size] ?? SIZE_CLASS_MAP.md;
  const textSizeClasses = TEXT_SIZE_CLASS_MAP[size] ?? TEXT_SIZE_CLASS_MAP.md;
  const statusColor = STATUS_COLOR_MAP[status];

  return (
    <div className={cn('relative flex-shrink-0', className)}>
      <div
        className={cn(
          'relative rounded-full overflow-hidden bg-muted flex items-center justify-center',
          sizeClasses,
          !shouldRenderImageContainer && 'bg-primary/10 text-primary',
          wrapperClassName,
        )}
      >
        {shouldRenderImageContainer ? (
          <>
            <Image
              ref={imageRef}
              src={effectiveSrc}
              alt={alt}
              className={cn(
              'w-full h-full object-cover transition-opacity duration-300',
              shouldShowImage ? 'opacity-100' : 'opacity-0',
            )}
            onLoad={() => {
              if (effectiveSrc) {
                loadedAvatarSources.add(effectiveSrc);
              }
              setIsImageLoaded(true);
            }}
            onError={() => {
              setHasError(true);
              setIsImageLoaded(false);
            }}
          />
            {!shouldShowImage ? (
              <div
                className="absolute inset-0 flex items-center justify-center bg-muted/80 text-primary"
                role="status"
                aria-live="polite"
              >
                <span
                  aria-hidden="true"
                  className="h-6 w-6 rounded-full border-2 border-muted-foreground/30 border-t-primary animate-spin"
                />
                <span className="sr-only">Loading avatar</span>
              </div>
            ) : null}
          </>
        ) : (
          <span className={cn('font-semibold uppercase text-primary', textSizeClasses)}>
            {initials || '?'}
          </span>
        )}
      </div>
      {showStatus && statusColor && (
        <div
          className={cn(
            'absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-card',
            statusColor,
          )}
        />
      )}
    </div>
  );
};

export default EmployeeAvatar;
